import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { DatePipe } from '@angular/common';

import { Router } from '@angular/router';
import { StorageService } from '@core/services/util/storage.service';
import { HeaderState } from '@shared/models/util';

import { environment } from '@environment';
import { ToastrService } from '../notification/toastr.service';
import { PrivacyPolicyApiResponse } from '@app/shared/models/api-response/privacy-policy';
import { Outlet, OutletState } from '@app/shared/models/api-response/catalog-api-response';
import { Menu } from '@app/shared/models/api-response/outlet-timing-response';

@Injectable({
    providedIn: 'root'
})
export class UtilityService {
    phoneRegex = /^[6-9]\d{9}$/;
    emailRegex = /^[a-zA-Z0-9\-\._%\+]{1,256}@[a-zA-Z0-9][a-zA-Z0-9\-]{0,64}(\.[a-zA-Z0-9][a-zA-Z0-9\-]{0,25})+$/;
    currentHeaderState = new BehaviorSubject<HeaderState>(new HeaderState());

    constructor(
        private storageService: StorageService,
        private http: HttpClient,
        private router: Router,
        private toastrService: ToastrService,
        private datePipe: DatePipe
    ) {}

    generateRandomString(size: number) {
        let text = '';
        const candidate_alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
        for (let i = 0; i < size; i++) {
            text += candidate_alphabet.charAt(Math.floor(Math.random() * candidate_alphabet.length));
        }
        return text;
    }

    generateRandomDeviceId() {
        return (
            this.generateRandomString(8) +
            '-' +
            this.generateRandomString(4) +
            '-' +
            this.generateRandomString(4) +
            '-' +
            this.generateRandomString(4) +
            '-' +
            this.generateRandomString(12)
        );
    }

    getDefaultCookieExpiryTime() {
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + 7);
        return expiryDate;
    }

    sortBySequence(array: any) {
        return array.sort((first, second) => first.sequence - second.sequence);
    }

    getV2GlobalHeaders(text = null) {
        return {
            Accept: `application/${text || 'vnd.phpl.v2'}`
        };
    }

    getAuthHeaders(type?: string) {
        let sessionId = null;

        switch (type) {
            case 'signed':
                sessionId = this.storageService.get('session-id');
                break;
            case 'unsigned':
                sessionId = this.storageService.get('unsigned_session_id');
                break;
            default:
                sessionId = this.storageService.get('session-id') || this.storageService.get('unsigned_session_id');
                break;
        }

        return {
            Authorization: 'Bearer ' + sessionId
        };
    }

    markFormFieldsAsTouched(form) {
        Object.keys(form.controls).forEach(field => {
            const control = form.get(field);
            control.markAsTouched({ onlySelf: true });
        });
    }

    // Incase if this fails in future because its in a service, create a subscriber place it in header component and route from there
    locationBasedRedirect() {
        const brandId = environment.brand.id;
        const outletId = this.storageService.get('outlet_id');

        if (brandId === 19) {
            this.router.navigateByUrl('/');
            return false;
        }

        if (!outletId || Number(outletId) === 1) {
            this.router.navigateByUrl('/');
            return false;
        } else {
            this.router.navigateByUrl('/menu');
            return false;
        }
    }

    logData(data, type) {
        const headers = { headers: this.getAuthHeaders() };
        this.http
            .post(
                `${environment.webApiUrl}/log_data`,
                {
                    result: {
                        brandId: environment.brand.id,
                        ...data
                    },
                    path: type
                },
                headers
            )
            .toPromise()
            .catch(err => {
                console.log(err);
            });
    }

    hexToRGB(hex: string, opacity = 1) {
        var hex = hex.replace('#', '');
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return 'rgba' + '(' + r + ',' + g + ',' + b + ',' + opacity + ')';
    }

    getBrandState({ active, open, preorder_available }): string {
        let state: string;

        if (active) {
            if (open) {
                state = 'active';
            } else if (preorder_available) {
                state = 'preorder';
            } else {
                state = 'closed';
            }
        } else {
            state = 'closed';
        }

        return state;
    }

    formattedDate(date, format) {
        // input date format - '1653583668'
        return this.datePipe.transform(new Date(date * 1000).getTime(), format);
    }

    dateDifference(date1: Date, date2?: Date): number {
        // If date2 is not provided, use the current date
        const endDate = date2 ? date2 : new Date();
        const diffTime = Math.abs(endDate.getTime() - date1.getTime());
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    }

    copyCode(value: any, toastMessage = 'Code copied!') {
        const el = document.createElement('textarea');
        el.value = value;
        el.setAttribute('readonly', '');
        el.style.position = 'absolute';
        el.style.left = '-9999px';
        document.body.appendChild(el);
        el.select();

        // Todo: remove this deprecated code, 05/08/24
        document.execCommand('copy');
        document.body.removeChild(el);
        this.toastrService.openSnackBar(toastMessage, 'OKAY');
    }

    getTnC(tncName: string) {
        return this.http.get(environment.apiUrl + '/static_webpage/show?name=' + tncName, {
            responseType: 'text'
        });
    }

    getPrivacyPolicy(): Observable<PrivacyPolicyApiResponse> {
        return this.http.get<PrivacyPolicyApiResponse>(`${environment.apiUrl}/v1/terms_and_conditions/get_privacy_policy`, {
            params: {
                brand_id: environment.brand.id
            }
        });
    }

    allowOnlyNumbers(event: KeyboardEvent | ClipboardEvent): boolean {
        if (event.type === 'keypress') {
            const char = (event as KeyboardEvent).key;

            // Allow numbers and Enter -> Enter if user presses enter after entering a number
            if (/^[0-9]$/.test(char) || char === 'Enter') {
                return true;
            }

            // Prevent any other keys
            event.preventDefault();
            return false;
        } else if (event.type === 'paste') {
            const pastedData = (event as ClipboardEvent).clipboardData.getData('Text');

            // If the pasted data contains any non-numeric character, prevent the paste
            if (/[^0-9]/.test(pastedData)) {
                event.preventDefault();
                return false;
            }
        }

        return true;
    }

    private deg2rad(deg: number): number {
        return deg * (Math.PI / 180);
    }

    getDistanceFromLatLonInMeter(lat1: any, lon1: any, lat2: number, lon2: number): number {
        const R = 6371; // Radius of the earth in km
        const dLat = this.deg2rad(lat2 - lat1);
        const dLon = this.deg2rad(lon2 - lon1);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const d = R * c * 1000; // Distance in meters
        return d;
    }

    getRemainingTime(data) {
        const sobTime = new Date(data.sob_time) as any;
        const currentTime = new Date() as any;
        return sobTime - currentTime;
    }

    getOutletState(outlet: Outlet | Menu): string {
        const { active, dflt, open, outlet_timings, preorder_available } = outlet;
        const outletLocality = this.storageService.get('outlet_locality');

        const remainingTime = Array.isArray(outlet_timings)
            ? this.getRemainingTime(outlet_timings[0])
            : this.getRemainingTime(outlet_timings);

        if (!active) return OutletState.CLOSED;
        if (dflt && outletLocality !== 'Showing Default Location') return OutletState.OUT_OF_REACH;
        if (!open) return remainingTime > 0 && preorder_available ? OutletState.PREORDER : OutletState.PAUSED;

        return OutletState.ACTIVE;
    }

    openLocationSearchBar() {
        const headerState = this.currentHeaderState.value;
        headerState.showSearchBox = true;
        this.currentHeaderState.next(headerState);
    }

    getUrlParameter(url: string, paramName: string): string | null {
        const regex = new RegExp(`[?&]${paramName}=([^&]*)`);
        const match = url.match(regex);
        return match ? match[1] : null;
    }
}
